import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import api from "./lib/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Layout from "./components/Layout";
import AIAssistant from "./components/AIAssistant";
import ResourcePage from "./components/ResourcePage";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Documents from "./pages/Documents";
import Reports from "./pages/Reports";
import Notifications from "./pages/Notifications";
import AuditLog from "./pages/AuditLog";
import UsersAdmin from "./pages/UsersAdmin";
import Calendar from "./pages/Calendar";
import Settings from "./pages/Settings";
import AIAssistantPage from "./pages/AIAssistantPage";
import ZellePayments from "./pages/ZellePayments";
import RentReminders from "./pages/RentReminders";
import BankTransactions from "./pages/BankTransactions";
import LeasesView from "./pages/LeasesView";
import TenantsView from "./pages/TenantsView";
import Checklists from "./pages/Checklists";
import Compliance from "./pages/Compliance";
import Communication from "./pages/Communication";
import PropertyDetail from "./pages/PropertyDetail";
import InstallPrompt from "./components/InstallPrompt";
import { Toaster } from "./components/ui/sonner";

const landingFor = (role) => (role === "contractor" ? "/maintenance" : "/");

function PendingApproval() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="pending-approval">
      <div className="max-w-md w-full text-center rounded-2xl border border-border bg-card p-8">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-white font-heading font-extrabold text-xl mb-4">K</div>
        <h1 className="font-heading text-2xl font-bold">Account awaiting approval</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Thanks for registering, {user?.name}. Your Kora Holding, LLC account is pending review.
          An administrator must grant you access before you can view portfolio data.
        </p>
        <button onClick={logout} data-testid="pending-logout" className="mt-6 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition-colors">
          Sign out
        </button>
      </div>
    </div>
  );
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        setUser(data);
        window.history.replaceState(null, "", "/");
        navigate("/");
      } catch {
        navigate("/login");
      }
    })();
  }, [navigate, setUser]);

  return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Signing you in…</div>;
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "pending") return <PendingApproval />;
  if (roles && !roles.includes(user.role)) return <Navigate to={landingFor(user.role)} replace />;
  return (
    <Layout>
      {children}
      {user.role !== "contractor" && <AIAssistant />}
    </Layout>
  );
}

const ALL = ["admin", "manager", "readonly", "contractor"];
const STAFF = ["admin", "manager", "readonly"];

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected roles={STAFF}><Dashboard /></Protected>} />
      <Route path="/calendar" element={<Protected roles={STAFF}><Calendar /></Protected>} />
      <Route path="/alerts" element={<Protected roles={STAFF}><Notifications /></Protected>} />
      <Route path="/ai" element={<Protected roles={STAFF}><AIAssistantPage /></Protected>} />
      <Route path="/properties" element={<Protected roles={ALL}><ResourcePage moduleKey="properties" /></Protected>} />
      <Route path="/properties/:id" element={<Protected roles={STAFF}><PropertyDetail /></Protected>} />
      <Route path="/units" element={<Navigate to="/leases" replace />} />
      <Route path="/tenants" element={<Protected roles={STAFF}><TenantsView /></Protected>} />
      <Route path="/leases" element={<Protected roles={STAFF}><LeasesView /></Protected>} />
      <Route path="/payments" element={<Protected roles={STAFF}><ResourcePage moduleKey="payments" /></Protected>} />
      <Route path="/zelle" element={<Protected roles={STAFF}><ZellePayments /></Protected>} />
      <Route path="/reminders" element={<Protected roles={STAFF}><RentReminders /></Protected>} />
      <Route path="/banking" element={<Protected roles={STAFF}><BankTransactions /></Protected>} />
      <Route path="/renewals" element={<Navigate to="/leases?tab=renewals" replace />} />
      <Route path="/checklists" element={<Protected roles={STAFF}><Checklists /></Protected>} />
      <Route path="/compliance" element={<Protected roles={STAFF}><Compliance /></Protected>} />
      <Route path="/messages" element={<Protected roles={STAFF}><Communication /></Protected>} />
      <Route path="/maintenance" element={<Protected roles={ALL}><ResourcePage moduleKey="maintenance" /></Protected>} />
      <Route path="/contractors" element={<Protected roles={STAFF}><ResourcePage moduleKey="contractors" /></Protected>} />
      <Route path="/insurance" element={<Protected roles={STAFF}><ResourcePage moduleKey="insurance" /></Protected>} />
      <Route path="/taxes" element={<Protected roles={STAFF}><ResourcePage moduleKey="taxes" /></Protected>} />
      <Route path="/mortgages" element={<Protected roles={STAFF}><ResourcePage moduleKey="mortgages" /></Protected>} />
      <Route path="/hoa" element={<Protected roles={STAFF}><ResourcePage moduleKey="hoa" /></Protected>} />
      <Route path="/utilities" element={<Protected roles={STAFF}><ResourcePage moduleKey="utilities" /></Protected>} />
      <Route path="/expenses" element={<Protected roles={STAFF}><ResourcePage moduleKey="expenses" /></Protected>} />
      <Route path="/documents" element={<Protected roles={STAFF}><Documents /></Protected>} />
      <Route path="/reports" element={<Protected roles={STAFF}><Reports /></Protected>} />
      <Route path="/settings" element={<Protected roles={STAFF}><Settings /></Protected>} />
      <Route path="/users" element={<Protected roles={["admin"]}><UsersAdmin /></Protected>} />
      <Route path="/audit" element={<Protected roles={["admin"]}><AuditLog /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <InstallPrompt />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
